import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, shareReplay } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../../environments/environment";
import { SKIP_ERROR_ALERT } from "../../shared/interceptors/skip-error-alert.context";

/**
 * "Delivery by DashyPay" (managed courier, provider Mon Coursier) — ERP endpoints.
 * Every call needs the Keycloak role DELIVERY_BY_DASHYPAY; the ERP answers 403 otherwise.
 */

export type ShipmentStatus =
  | "QUEUED" | "CREATING" | "UNKNOWN" | "RETRY" | "CREATED" | "IN_PROGRESS"
  | "DELIVERED" | "CANCELLED" | "FAILED" | "REJECTED" | "NEEDS_REVIEW" | "RETURNED";

export interface OrderShipment {
  reference: string;
  orderReference?: string;
  provider: string;
  environment: string;
  status: ShipmentStatus;
  externalStatus?: string | null;
  trackingNumber?: string | null;
  rateCardCode?: string | null;
  serviceType?: string | null;
  transportType?: string | null;
  packageType?: string | null;
  pickupLocationRef?: string | null;
  pickupLabel?: string | null;
  distanceKm?: number | null;
  estimatedPrice?: number | null;
  estimatedTotalPrice?: number | null;
  basePrice?: number | null;
  totalPrice?: number | null;
  pricingVersion?: number | null;
  externalPaymentStatus?: string | null;
  externalCancellationReason?: string | null;
  actualPickupTime?: string | null;
  actualDeliveryTime?: string | null;
  externalUpdatedAt?: string | null;
  lastSyncedAt?: string | null;
  attempts: number;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  requestedBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  closedAt?: string | null;
  /** Live tracking, filled by the courier position pings (all null until the courier shares a position). */
  liveDistanceM?: number | null;
  liveEtaMinutes?: number | null;
  liveLat?: number | null;
  liveLng?: number | null;
  liveAt?: string | null;
  courierName?: string | null;
  courierPhone?: string | null;
}

/** One status change or position ping of a shipment (oldest first). `status` is the lower-case courier word. */
export interface ShipmentEvent {
  status?: string | null;
  distanceM?: number | null;
  etaMinutes?: number | null;
  lat?: number | null;
  lng?: number | null;
  channel?: string | null;
  occurredAt: string;
}

/** A delivery partner the platform lets this company see (Dashy controls status, visibility and scope). */
export interface CourierPartner {
  provider: string;
  name: string;
  logoUrl?: string | null;
  /** ENABLED, COMING_SOON (DISABLED partners are never returned). */
  status: "ENABLED" | "COMING_SOON" | "DISABLED";
  usable: boolean;
  /** The company's delivery method behind this partner (null until provisioned), and its switch. */
  methodReference?: string | null;
  methodActive?: boolean | null;
  /** How this company's in-app shows the method (editable per company). */
  methodNameAr?: string | null;
  methodNameFr?: string | null;
  methodNameEn?: string | null;
  methodEstimatedTime?: string | null;
  /** Orders are sent to this partner automatically (default) or by hand. */
  autoSend?: boolean | null;
  /** What the partner API can do: the UI hides actions it cannot perform (e.g. Cancel). */
  supportsCancel?: boolean;
  supportsWebhook?: boolean;
  supportsPolling?: boolean;
}

/** A partner as the admin switchboard returns it. */
export interface AdminCourierPartner {
  provider: string;
  name: string;
  logoUrl?: string | null;
  status: "ENABLED" | "DISABLED" | "COMING_SOON";
  visible: boolean;
  /** ALL organisations, or only the SELECTED ones (allowedCompanyIds). */
  scope: "ALL" | "SELECTED";
  allowedCompanyIds?: string[] | null;
  supportsCancel: boolean;
  supportsWebhook: boolean;
  supportsPolling: boolean;
  /** Read side: a callback secret is stored. The secret itself is never returned. */
  hasCallbackSecret?: boolean;
  /** Write side: sets or replaces the callback secret. */
  callbackSecret?: string;
}

export interface PickupOption {
  locationRef: string;
  label: string;
  hasPin: boolean;
  distanceKm?: number | null;
  estimatedTotalPrice?: number | null;
}

export interface ShipmentEstimate {
  orderReference: string;
  eligible: boolean;
  /** i18n keys COURIER.* (see COURIER.REASON in the translations). */
  reasons: string[];
  rateCardCode?: string | null;
  rateCardName?: string | null;
  pickupLocationRef?: string | null;
  pickupLabel?: string | null;
  pickupLat?: number | null;
  pickupLng?: number | null;
  deliveryLat?: number | null;
  deliveryLng?: number | null;
  straightLineKm?: number | null;
  distanceKm?: number | null;
  estimatedPrice?: number | null;
  estimatedTotalPrice?: number | null;
  vatRate?: number | null;
  pickupOptions: PickupOption[];
  shipment?: OrderShipment | null;
}

export interface ShipmentRequestOptions {
  pickupLocationRef?: string | null;
  packageType?: string | null;
  deliveryInstructions?: string | null;
}

export interface CourierPickupConfig {
  pickupAddress?: string | null;
  pickupCity?: string | null;
  pickupLat?: number | null;
  pickupLng?: number | null;
  pickupContactName?: string | null;
  pickupPhone?: string | null;
  pickupInstructions?: string | null;
  /** Send eligible orders to the courier automatically when they are created (default on). */
  autoSend?: boolean | null;
  complete?: boolean;
  disabledAt?: string | null;
}

/** A sale point as a pickup point: ticked (= has a pin) means its orders are collected there. */
export interface SalePointPickup {
  reference: string;
  name: string;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  active: boolean;
  included: boolean;
  pickupLat?: number | null;
  pickupLng?: number | null;
}

export interface CourierRateCard {
  reference: string;
  code: string;
  name: string;
  transportType: string;
  deliveryType: string;
  packageTypeDefault: string;
  basePrice: number;
  includedKm: number;
  pricePerKm: number;
  surcharge: number;
  flatPrice?: number | null;
  vatRate: number;
  active: boolean;
}

export interface ApiEnvelope<T> {
  body?: T;
  status?: string;
  message?: string;
  i18nErrorCode?: string;
}

@Injectable({ providedIn: "root" })
export class CourierService {
  private readonly erp = environment.erpApiUrl.replace(/\/$/, "");
  /** Errors are rendered inside the courier cards, not as a global toast. */
  private readonly quiet = new HttpContext().set(SKIP_ERROR_ALERT, true);

  private partnerList$?: Observable<CourierPartner[]>;

  constructor(private readonly http: HttpClient) {}

  /** The partners of this company, fetched once per page load and shared (they rarely change). */
  partnerList(): Observable<CourierPartner[]> {
    if (!this.partnerList$) {
      this.partnerList$ = this.partners().pipe(catchError(() => of([] as CourierPartner[])), shareReplay(1));
    }
    return this.partnerList$;
  }

  /**
   * True when the platform lets this company use at least one delivery partner. Replaces the old Keycloak role
   * check: Dashy decides per organisation from the switchboard.
   */
  isEntitled(): Observable<boolean> {
    return this.partnerList().pipe(map((list) => list.some((p) => p.status === "ENABLED" && p.usable)));
  }

  estimate(orderRef: string, pickupLocationRef?: string | null): Observable<ApiEnvelope<ShipmentEstimate>> {
    const query = pickupLocationRef ? `?pickupLocationRef=${encodeURIComponent(pickupLocationRef)}` : "";
    return this.http.get<ApiEnvelope<ShipmentEstimate>>(
      `${this.erp}/v1/api/orders/${encodeURIComponent(orderRef)}/shipment/estimate${query}`,
      { headers: this.headers(), context: this.quiet },
    );
  }

  estimates(orderRefs: string[]): Observable<ApiEnvelope<Record<string, ShipmentEstimate>>> {
    return this.http.post<ApiEnvelope<Record<string, ShipmentEstimate>>>(
      `${this.erp}/v1/api/orders/shipment/estimates`, orderRefs,
      { headers: this.headers(), context: this.quiet },
    );
  }

  send(orderRef: string, options: ShipmentRequestOptions): Observable<ApiEnvelope<OrderShipment>> {
    return this.http.post<ApiEnvelope<OrderShipment>>(
      `${this.erp}/v1/api/orders/${encodeURIComponent(orderRef)}/shipment`, options ?? {},
      { headers: this.headers(), context: this.quiet },
    );
  }

  /** Cancels the live shipment: closed here when still queued, else asked of the partner (cancel capability). */
  cancel(orderRef: string, reason?: string): Observable<ApiEnvelope<OrderShipment>> {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    return this.http.post<ApiEnvelope<OrderShipment>>(
      `${this.erp}/v1/api/orders/${encodeURIComponent(orderRef)}/shipment/cancel${query}`, {},
      { headers: this.headers(), context: this.quiet },
    );
  }

  latest(orderRef: string): Observable<ApiEnvelope<OrderShipment> | null> {
    return this.http.get<ApiEnvelope<OrderShipment> | null>(
      `${this.erp}/v1/api/orders/${encodeURIComponent(orderRef)}/shipment`,
      { headers: this.headers(), context: this.quiet },
    );
  }

  history(orderRef: string): Observable<ApiEnvelope<OrderShipment[]>> {
    return this.http.get<ApiEnvelope<OrderShipment[]>>(
      `${this.erp}/v1/api/orders/${encodeURIComponent(orderRef)}/shipment/history`,
      { headers: this.headers(), context: this.quiet },
    );
  }

  events(orderRef: string): Observable<ApiEnvelope<ShipmentEvent[]>> {
    return this.http.get<ApiEnvelope<ShipmentEvent[]>>(
      `${this.erp}/v1/api/orders/${encodeURIComponent(orderRef)}/shipment/events`,
      { headers: this.headers(), context: this.quiet },
    );
  }


  /** Partners visible to this company; a plain array, no role needed. */
  partners(): Observable<CourierPartner[]> {
    return this.http.get<CourierPartner[]>(`${this.erp}/v1/api/courier/partners`, {
      headers: this.headers(),
      context: this.quiet,
    });
  }

  getPickup(): Observable<ApiEnvelope<CourierPickupConfig>> {
    return this.http.get<ApiEnvelope<CourierPickupConfig>>(
      `${this.erp}/v1/api/courier/pickup`, { headers: this.headers(), context: this.quiet },
    );
  }

  updatePickup(config: CourierPickupConfig): Observable<ApiEnvelope<CourierPickupConfig>> {
    return this.http.put<ApiEnvelope<CourierPickupConfig>>(
      `${this.erp}/v1/api/courier/pickup`, config, { headers: this.headers(), context: this.quiet },
    );
  }

  salePointPickups(): Observable<ApiEnvelope<SalePointPickup[]>> {
    return this.http.get<ApiEnvelope<SalePointPickup[]>>(
      `${this.erp}/v1/api/courier/pickup/sale-points`, { headers: this.headers(), context: this.quiet },
    );
  }

  updateSalePointPickup(reference: string, body: Pick<SalePointPickup, "included" | "pickupLat" | "pickupLng">): Observable<ApiEnvelope<SalePointPickup>> {
    return this.http.put<ApiEnvelope<SalePointPickup>>(
      `${this.erp}/v1/api/courier/pickup/sale-points/${encodeURIComponent(reference)}`, body,
      { headers: this.headers(), context: this.quiet },
    );
  }

  // ---- Dashy admin (Solution area): no x-api headers, plain bodies, like the admin metrics APIs ----

  /** Platform switchboard: every partner with its status, visibility and organisation scope. */
  adminPartners(): Observable<AdminCourierPartner[]> {
    return this.http.get<AdminCourierPartner[]>(`${this.erp}/v1/api/admin-ui/courier/partners`, { context: this.quiet });
  }

  /** Creates a partner (it starts DISABLED, so nothing is exposed before its n8n adapter is ready). */
  adminCreatePartner(partner: Partial<AdminCourierPartner>): Observable<AdminCourierPartner> {
    return this.http.post<AdminCourierPartner>(`${this.erp}/v1/api/admin-ui/courier/partners`, partner, { context: this.quiet });
  }

  adminUpdatePartner(provider: string, partner: Partial<AdminCourierPartner>): Observable<AdminCourierPartner> {
    return this.http.put<AdminCourierPartner>(
      `${this.erp}/v1/api/admin-ui/courier/partners/${encodeURIComponent(provider)}`, partner, { context: this.quiet },
    );
  }

  adminRateCards(): Observable<CourierRateCard[]> {
    return this.http.get<CourierRateCard[]>(`${this.erp}/v1/api/admin-ui/courier/rate-cards`, { context: this.quiet });
  }

  adminCreateRateCard(card: Partial<CourierRateCard>): Observable<CourierRateCard> {
    return this.http.post<CourierRateCard>(`${this.erp}/v1/api/admin-ui/courier/rate-cards`, card, { context: this.quiet });
  }

  adminUpdateRateCard(reference: string, card: Partial<CourierRateCard>): Observable<CourierRateCard> {
    return this.http.put<CourierRateCard>(`${this.erp}/v1/api/admin-ui/courier/rate-cards/${encodeURIComponent(reference)}`, card, { context: this.quiet });
  }

  adminCompanyVat(companyId: string): Observable<{ companyId: string; vatRate: number }> {
    return this.http.get<{ companyId: string; vatRate: number }>(
      `${this.erp}/v1/api/admin-ui/courier/companies/${encodeURIComponent(companyId)}/vat`, { context: this.quiet },
    );
  }

  adminSetCompanyVat(companyId: string, vatRate: number): Observable<{ companyId: string; vatRate: number }> {
    return this.http.put<{ companyId: string; vatRate: number }>(
      `${this.erp}/v1/api/admin-ui/courier/companies/${encodeURIComponent(companyId)}/vat`, { vatRate }, { context: this.quiet },
    );
  }

  adminDisableCompany(companyId: string): Observable<{ companyId: string; methodsDeactivated: number }> {
    return this.http.post<{ companyId: string; methodsDeactivated: number }>(
      `${this.erp}/v1/api/admin-ui/courier/companies/${encodeURIComponent(companyId)}/disable`, {}, { context: this.quiet },
    );
  }

  rateCards(provider?: string): Observable<ApiEnvelope<CourierRateCard[]>> {
    const query = provider ? `?provider=${encodeURIComponent(provider)}` : "";
    return this.http.get<ApiEnvelope<CourierRateCard[]>>(
      `${this.erp}/v1/api/courier/rate-cards${query}`, { headers: this.headers(), context: this.quiet },
    );
  }

  /** Automatic send to the partner: on by default, off means the merchant sends each order by hand. */
  setAutoSend(autoSend: boolean, provider?: string): Observable<ApiEnvelope<boolean>> {
    const query = provider ? `?provider=${encodeURIComponent(provider)}` : "";
    return this.http.put<ApiEnvelope<boolean>>(
      `${this.erp}/v1/api/courier/auto-send${query}`, { autoSend },
      { headers: this.headers(), context: this.quiet },
    );
  }

  /** The vehicle (rate card) every courier order of the company is sent with. */
  setDefaultVehicle(rateCardCode: string, provider?: string): Observable<ApiEnvelope<string>> {
    const query = provider ? `?provider=${encodeURIComponent(provider)}` : "";
    return this.http.put<ApiEnvelope<string>>(
      `${this.erp}/v1/api/courier/default-vehicle${query}`, { rateCardCode },
      { headers: this.headers(), context: this.quiet },
    );
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({ "x-api-canal": "web", "x-api-requestId": uuidv4() });
  }
}
